import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@core-ui-lib/utils';
import {
  SESSION_CONTACT_STATUSES,
  type SessionContactStatus,
  type SessionStatusCounts,
} from '@features/wizard/types/wizard.types';
import { StatusFilterChipsSkeleton } from './SessionsSkeletons';

interface StatusFilterChipsProps {
  counts: SessionStatusCounts;
  activeFilters: SessionContactStatus[];
  onToggle: (status: SessionContactStatus) => void;
  onClearAll: () => void;
  isLoading?: boolean;
}

const STATUS_COLOR_CLASSES: Record<SessionContactStatus, { dot: string; active: string }> = {
  noAnswer: {
    dot: 'bg-cyan-500',
    active: 'border-cyan-500/50 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  },
  cutOff: {
    dot: 'bg-red-500',
    active: 'border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400',
  },
  transferError: {
    dot: 'bg-amber-500',
    active: 'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  voicemail: {
    dot: 'bg-purple-500',
    active: 'border-purple-500/50 bg-purple-500/10 text-purple-600 dark:text-purple-400',
  },
  fuegoLead: {
    dot: 'bg-orange-500',
    active: 'border-orange-500/50 bg-orange-500/10 text-orange-600 dark:text-orange-400',
  },
};

const StatusFilterChips: React.FC<StatusFilterChipsProps> = ({
  counts,
  activeFilters,
  onToggle,
  onClearAll,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <StatusFilterChipsSkeleton />;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground mr-1">
        {t('wizard.selectContacts.sessions.includeLabel', 'Include:')}
      </span>

      {SESSION_CONTACT_STATUSES.map((status) => {
        const isActive = activeFilters.includes(status);
        const count = counts[status];
        const isDisabled = count === 0;
        const colors = STATUS_COLOR_CLASSES[status];

        return (
          <button
            key={status}
            type="button"
            disabled={isDisabled}
            onClick={() => {
              onToggle(status);
            }}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border',
              'border-border bg-transparent text-muted-foreground',
              'hover:border-muted-foreground hover:text-foreground',
              isActive && colors.active,
              isDisabled && 'opacity-40 cursor-not-allowed pointer-events-none',
            )}
          >
            <span className={cn('h-2 w-2 rounded-full', colors.dot)} />
            {t(`wizard.selectContacts.sessions.filter.${status}`, status)}
            <span className="text-[10px] bg-foreground/10 text-muted-foreground px-1.5 py-0.5 rounded-full tabular-nums">
              {count}
            </span>
          </button>
        );
      })}

      {activeFilters.length > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground ml-auto"
        >
          {t('wizard.selectContacts.sessions.filter.clearAll', 'Clear all')}
        </button>
      )}
    </div>
  );
};

export default StatusFilterChips;
